import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { useAuth } from '@/contexts/AuthContext';
import { Event, RSVP, Invoice, Payment, User } from '@/types';
import { formatDate, formatCurrency, cn } from '@/lib/utils';
import { ArrowLeft, Ban, Download, Eye, Search } from 'lucide-react';
import { showError, showSuccess, confirmAction } from '@/lib/swal';
import { mapEventData, mapRsvpData, isActiveInvoiceData, getRsvpExpectedAmount } from '@/lib/event-utils';
import { getEffectiveAmountPaid } from '@/lib/invoice-utils';
import { getHeadlinePayableDueForDashboard, orderRsvpsForInvoice } from '@/lib/invoice-payment-breakdown';

/** One line in the table: either a paymentHistory entry or money recorded without line items (e.g. dashboard "Mark as paid"). */
interface EventPaymentRow {
  key: string;
  invoiceId: string;
  invoiceLabel: string;
  payerLabel: string;
  date: Date;
  amount: number;
  methodLabel: string;
  reference: string;
  /** Index into invoice.paymentHistory when this row is a stored payment line. */
  paymentHistoryIndex?: number;
  syntheticKind?: 'remainder' | 'fallback';
  /** For fallback-only rows, the RSVP document id. */
  fallbackRsvpId?: string;
  voided?: boolean;
}

interface ResolvedPaymentDetails {
  invoice: Invoice | null;
  rsvps: RSVP[];
  payment: Payment | null;
  syntheticNote: string | null;
}

function formatRsvpDisplayName(rsvp: RSVP): string {
  const parts = [rsvp.rank, rsvp.name].filter(Boolean);
  return parts.join(' ').trim() || 'Guest';
}

function paymentMethodLabel(method: Payment['method']): string {
  switch (method) {
    case 'bank_transfer':
      return 'Bank transfer';
    case 'stripe':
      return 'Stripe';
    case 'sumup':
      return 'SumUp';
    case 'slickpay':
      return 'SlickPay';
    case 'card':
    default:
      return 'Card';
  }
}

function paymentReference(payment: Payment): string {
  return (
    payment.transactionId ||
    payment.stripePaymentIntentId ||
    payment.sumupCheckoutId ||
    payment.slickpayInvoiceId ||
    'n/a'
  );
}

const AMOUNT_EPS = 0.01;

function escapeCsvCell(value: string): string {
  const normalised = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (normalised.includes(',') || normalised.includes('"') || normalised.includes('\n')) {
    return `"${normalised.replace(/"/g, '""')}"`;
  }
  return normalised;
}

function buildPaymentsCsv(rows: EventPaymentRow[]): string {
  const headers = ['Date', 'Payer', 'Invoice', 'Method', 'Amount (GBP)', 'Reference'];
  const headerLine = headers.map(escapeCsvCell).join(',');
  const dataLines = rows.map((row) =>
    [
      formatDate(row.date),
      row.payerLabel,
      row.invoiceLabel,
      row.methodLabel,
      row.amount.toFixed(2),
      row.reference,
    ]
      .map((cell) => escapeCsvCell(String(cell)))
      .join(',')
  );
  return [headerLine, ...dataLines].join('\r\n');
}

function slugifyFilenameSegment(name: string): string {
  const s = name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');
  return s.length > 0 ? s.slice(0, 64) : 'payments';
}

function isRsvpMarkedPaid(rsvp: RSVP): boolean {
  if (rsvp.apologies) {
    return false;
  }
  if (rsvp.paid === true) {
    return true;
  }
  const ps = (rsvp.paymentStatus || '').trim().toLowerCase();
  return ps === 'paid' || ps === 'fully paid';
}

function resolvePaymentRowDetails(
  row: EventPaymentRow,
  invoices: Invoice[],
  rsvpMap: Map<string, RSVP>
): ResolvedPaymentDetails {
  let invoice: Invoice | null = invoices.find((i) => i.id === row.invoiceId) ?? null;

  if (!invoice && row.fallbackRsvpId) {
    const rsvp = rsvpMap.get(row.fallbackRsvpId);
    if (rsvp?.invoiceId) {
      invoice = invoices.find((i) => i.id === rsvp.invoiceId) ?? null;
    }
  }

  let rsvps: RSVP[] = [];
  if (row.fallbackRsvpId) {
    const r = rsvpMap.get(row.fallbackRsvpId);
    if (r) {
      rsvps = [r];
    }
  } else if (invoice) {
    rsvps = (invoice.rsvps || [])
      .map((id) => rsvpMap.get(id))
      .filter((x): x is RSVP => Boolean(x));
  }

  let payment: Payment | null = null;
  if (
    row.paymentHistoryIndex !== undefined &&
    invoice &&
    invoice.paymentHistory[row.paymentHistoryIndex]
  ) {
    payment = invoice.paymentHistory[row.paymentHistoryIndex];
  }

  let syntheticNote: string | null = null;
  if (payment === null) {
    if (row.syntheticKind === 'remainder') {
      syntheticNote =
        'This line represents money recorded on the invoice (for example, marked as paid manually) that is not itemised as a separate payment history entry.';
    } else if (row.syntheticKind === 'fallback') {
      syntheticNote =
        'This line is derived from the guest payment state when no matching invoice detail line was available.';
    }
  }

  return { invoice, rsvps, payment, syntheticNote };
}

/** Controls which technical, audit, and platform fields are shown in the payment details modal. */
interface PaymentDetailsVisibility {
  /** Platform fee splits and cumulative platform profit (treasurer or platform admin only). */
  showPlatformAccounting: boolean;
  /** Firestore document ids, user ids, owner uids, internal references. */
  showInternalIdentifiers: boolean;
  /** Invitation tokens and similar secrets. */
  showSecuritySensitive: boolean;
  /** Soft-delete audit fields. */
  showAuditFields: boolean;
  /** Stripe / SumUp / gateway reference ids on payment lines. */
  showPaymentProviderRefs: boolean;
}

/**
 * Stewards and members see a reduced view suitable for event organisation.
 * Secretaries see operational identifiers; treasurers and admins see financial platform data too.
 */
function getPaymentDetailsVisibility(unitId: string | undefined, userProfile: User | null | undefined): PaymentDetailsVisibility {
  if (!unitId) {
    return {
      showPlatformAccounting: false,
      showInternalIdentifiers: false,
      showSecuritySensitive: false,
      showAuditFields: false,
      showPaymentProviderRefs: false,
    };
  }
  const role = userProfile?.units?.find((u) => u.unitId === unitId)?.role;
  const admin = userProfile?.isAdmin === true;
  const treasurer = admin || role === 'Treasurer';
  const secretaryOrAbove = treasurer || role === 'Secretary';

  return {
    showPlatformAccounting: treasurer,
    showInternalIdentifiers: secretaryOrAbove,
    showSecuritySensitive: secretaryOrAbove,
    showAuditFields: secretaryOrAbove,
    showPaymentProviderRefs: secretaryOrAbove,
  };
}

function EmptyValue() {
  return <span className="text-muted-foreground">—</span>;
}

function displayText(value: unknown): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (value instanceof Date) {
    return formatDate(value);
  }
  return String(value);
}

function displayMono(value: unknown): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }
  const str = value instanceof Date ? formatDate(value) : String(value);
  return <span className="font-mono text-xs break-all">{str}</span>;
}

function DetailField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      <div className="text-xs font-medium leading-tight text-muted-foreground">{label}</div>
      <div className="text-sm leading-snug text-foreground">{children}</div>
    </div>
  );
}

function SectionBlock({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border border-border/80 bg-muted/15 p-4', className)}>
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {children}
    </div>
  );
}

function formatNestedValue(val: unknown, depth: number): ReactNode {
  if (depth > 2) {
    return <span className="font-mono text-xs break-all">{JSON.stringify(val)}</span>;
  }
  if (val === null || val === undefined) {
    return <EmptyValue />;
  }
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
    return String(val);
  }
  if (val instanceof Date) {
    return formatDate(val);
  }
  if (Array.isArray(val)) {
    if (val.length === 0) {
      return <span className="text-muted-foreground">None</span>;
    }
    return (
      <ul className="list-inside list-disc space-y-1 text-xs">
        {val.map((item, i) => (
          <li key={i} className="break-words">
            {typeof item === 'object' && item !== null ? formatNestedValue(item, depth + 1) : String(item)}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof val === 'object') {
    return (
      <dl className="space-y-2 border-l-2 border-muted py-0.5 pl-3 text-xs">
        {Object.entries(val as Record<string, unknown>).map(([k, v]) => (
          <div key={k}>
            <dt className="font-medium text-muted-foreground">{k}</dt>
            <dd className="mt-0.5 break-words">{formatNestedValue(v, depth + 1)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return String(val);
}

function QuestionnaireResponsesUi({ responses }: { responses: RSVP['responses'] }) {
  if (!responses || Object.keys(responses).length === 0) {
    return <p className="text-sm text-muted-foreground">No questionnaire responses.</p>;
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {Object.entries(responses).map(([key, val]) => (
        <DetailField key={key} label={key}>
          {formatNestedValue(val, 0)}
        </DetailField>
      ))}
    </div>
  );
}

function RsvpDetailsUi({ rsvp, visibility }: { rsvp: RSVP; visibility: PaymentDetailsVisibility }) {
  const { showInternalIdentifiers, showSecuritySensitive, showAuditFields, showPaymentProviderRefs } = visibility;

  return (
    <div className="space-y-4">
      <SectionBlock title="Guest">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {showInternalIdentifiers ? (
            <>
              <DetailField label="Document id">{displayMono(rsvp.id)}</DetailField>
              <DetailField label="Event id">{displayMono(rsvp.eventId)}</DetailField>
            </>
          ) : null}
          <DetailField label="Display name">{displayText(rsvp.name)}</DetailField>
          <DetailField label="First name">{displayText(rsvp.firstName)}</DetailField>
          <DetailField label="Last name">{displayText(rsvp.lastName)}</DetailField>
          <DetailField label="Prefix / rank">{displayText(rsvp.rank)}</DetailField>
          <DetailField label="Rank (short)">{displayText(rsvp.rankShortName)}</DetailField>
          <DetailField label="Additional rank">{displayText(rsvp.additionalRank)}</DetailField>
          <DetailField label="Unit">{displayText(rsvp.unit)}</DetailField>
        </div>
      </SectionBlock>
      <SectionBlock title="Contact and account">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Email">{displayText(rsvp.email)}</DetailField>
          {showInternalIdentifiers ? <DetailField label="User id">{displayMono(rsvp.userId)}</DetailField> : null}
        </div>
      </SectionBlock>
      <SectionBlock title="RSVP status">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Status">
            {rsvp.status ? <Badge variant="secondary">{rsvp.status}</Badge> : <EmptyValue />}
          </DetailField>
          <DetailField label="Apologies">{displayText(rsvp.apologies)}</DetailField>
          <DetailField label="Paid (flag)">{displayText(rsvp.paid)}</DetailField>
          <DetailField label="Payment status">{displayText(rsvp.paymentStatus)}</DetailField>
          <DetailField label="Payment method">{displayText(rsvp.paymentMethod)}</DetailField>
          {showPaymentProviderRefs ? (
            <DetailField label="SumUp checkout id">{displayMono(rsvp.sumupCheckoutId)}</DetailField>
          ) : null}
          {showPaymentProviderRefs ? (
            <DetailField label="SlickPay invoice id">{displayMono(rsvp.slickpayInvoiceId)}</DetailField>
          ) : null}
        </div>
      </SectionBlock>
      <SectionBlock title="Invoices and invitations">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {showInternalIdentifiers ? <DetailField label="Invoice id">{displayMono(rsvp.invoiceId)}</DetailField> : null}
          {showInternalIdentifiers ? <DetailField label="Invited by">{displayMono(rsvp.invitedBy)}</DetailField> : null}
          <DetailField label="Inviter pays">{displayText(rsvp.inviterPaysFor)}</DetailField>
          {showSecuritySensitive ? (
            <DetailField label="Invitation token">{displayMono(rsvp.invitationToken)}</DetailField>
          ) : null}
          <DetailField label="Ticket filter">{displayText(rsvp.ticketFilter)}</DetailField>
          <DetailField label="Decline reason">{displayText(rsvp.declineReason)}</DetailField>
        </div>
      </SectionBlock>
      <SectionBlock title="Attendance and preferences">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Checked in">{displayText(rsvp.checkedIn)}</DetailField>
          <DetailField label="Check-in time">{displayText(rsvp.checkInTime)}</DetailField>
          <DetailField label="Special requests">{displayText(rsvp.specialRequests)}</DetailField>
          <DetailField label="Plus one allowed">{displayText(rsvp.plusOneAllowed)}</DetailField>
          <DetailField label="Custom price">
            {rsvp.customPrice != null ? formatCurrency(rsvp.customPrice) : <EmptyValue />}
          </DetailField>
          <DetailField label="Tag">{displayText(rsvp.tag)}</DetailField>
        </div>
      </SectionBlock>
      {showInternalIdentifiers ? (
        <SectionBlock title="Flags">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Admin / test RSVP">{displayText(rsvp.isAdmin)}</DetailField>
            <DetailField label="Skip email">{displayText(rsvp.skipEmail)}</DetailField>
          </div>
        </SectionBlock>
      ) : null}
      <SectionBlock title="Questionnaire responses">
        <QuestionnaireResponsesUi responses={rsvp.responses} />
      </SectionBlock>
      <SectionBlock title="Record timestamps">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Created">{displayText(rsvp.createdAt)}</DetailField>
          <DetailField label="Updated">{displayText(rsvp.updatedAt)}</DetailField>
          {showAuditFields ? (
            <>
              <DetailField label="Deleted at">{displayText(rsvp.deletedAt)}</DetailField>
              <DetailField label="Deleted by uid">{displayMono(rsvp.deletedByUid)}</DetailField>
            </>
          ) : null}
        </div>
      </SectionBlock>
    </div>
  );
}

function invoiceStatusBadge(status: Invoice['status']) {
  switch (status) {
    case 'paid':
      return <Badge className="bg-green-600 hover:bg-green-600">{status}</Badge>;
    case 'partial':
      return <Badge variant="secondary">{status}</Badge>;
    case 'pending_verification':
      return <Badge className="bg-amber-600 hover:bg-amber-600">{status}</Badge>;
    case 'voided':
      return <Badge variant="destructive">{status}</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function InvoiceDetailsUi({
  invoice,
  visibility,
  rsvpById,
  event,
  allRsvps,
}: {
  invoice: Invoice;
  visibility: PaymentDetailsVisibility;
  rsvpById: Map<string, RSVP>;
  event: Event | null;
  allRsvps: RSVP[];
}) {
  const { showPlatformAccounting, showInternalIdentifiers, showAuditFields, showPaymentProviderRefs } = visibility;

  const ordered = orderRsvpsForInvoice(invoice, allRsvps);
  const headlineDue =
    event && ordered.length > 0
      ? getHeadlinePayableDueForDashboard(invoice, { event, rsvps: ordered })
      : null;

  return (
    <div className="space-y-4">
      <SectionBlock title="Identification">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {showInternalIdentifiers ? (
            <>
              <DetailField label="Document id">{displayMono(invoice.id)}</DetailField>
              <DetailField label="Event id">{displayMono(invoice.eventId)}</DetailField>
              <DetailField label="Unit id">{displayMono(invoice.unitId)}</DetailField>
              <DetailField label="Legacy lodge id">{displayMono(invoice.lodgeId)}</DetailField>
              <DetailField label="Owner uid">{displayMono(invoice.ownerUid)}</DetailField>
            </>
          ) : null}
          <DetailField label="Invoice number">{displayMono(invoice.invoiceNumber)}</DetailField>
        </div>
      </SectionBlock>
      <SectionBlock title="Amounts">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Total">{formatCurrency(invoice.totalAmount)}</DetailField>
          <DetailField label="Amount paid">{formatCurrency(invoice.amountPaid)}</DetailField>
          <DetailField label="Outstanding (stored)">{formatCurrency(invoice.outstandingBalance)}</DetailField>
          {headlineDue !== null ? (
            <DetailField label="Amount due (current rules)">{formatCurrency(headlineDue)}</DetailField>
          ) : null}
          <DetailField label="Subtotal">
            {invoice.subtotal != null ? formatCurrency(invoice.subtotal) : <EmptyValue />}
          </DetailField>
          <DetailField label="VAT amount">
            {invoice.vatAmount != null ? formatCurrency(invoice.vatAmount) : <EmptyValue />}
          </DetailField>
          <DetailField label="VAT rate">
            {invoice.vatRate != null ? String(invoice.vatRate) : <EmptyValue />}
          </DetailField>
          <DetailField label="Transaction fee">
            {invoice.transactionFee != null ? formatCurrency(invoice.transactionFee) : <EmptyValue />}
          </DetailField>
        </div>
      </SectionBlock>
      <SectionBlock title="Status and links">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Status">{invoiceStatusBadge(invoice.status)}</DetailField>
          <DetailField label="Payment link">
            {invoice.paymentLink && showInternalIdentifiers ? (
              <a
                href={invoice.paymentLink}
                className="break-all text-primary underline-offset-4 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {invoice.paymentLink}
              </a>
            ) : invoice.paymentLink ? (
              <span className="text-sm text-muted-foreground">
                A payment link exists for this invoice. The full URL is only shown to the secretary, treasurer, or
                administrator.
              </span>
            ) : (
              <EmptyValue />
            )}
          </DetailField>
        </div>
      </SectionBlock>
      <SectionBlock title="Linked guests">
        {invoice.rsvps && invoice.rsvps.length > 0 ? (
          showInternalIdentifiers ? (
            <ul className="flex flex-wrap gap-2">
              {invoice.rsvps.map((id) => (
                <li key={id}>
                  <Badge variant="outline" className="font-mono text-xs">
                    {id}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="list-inside list-disc space-y-1 text-sm">
              {invoice.rsvps.map((id) => {
                const guest = rsvpById.get(id);
                return (
                  <li key={id}>
                    {guest ? formatRsvpDisplayName(guest) : 'Guest'}
                  </li>
                );
              })}
            </ul>
          )
        ) : (
          <p className="text-sm text-muted-foreground">None linked.</p>
        )}
      </SectionBlock>
      <SectionBlock title="Payment history on invoice">
        {invoice.paymentHistory && invoice.paymentHistory.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Method</TableHead>
                  {showPaymentProviderRefs ? <TableHead className="min-w-[140px]">Reference</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.paymentHistory.map((p, idx) => (
                  <TableRow key={`${invoice.id}-ph-${idx}`}>
                    <TableCell className="whitespace-nowrap text-xs">{formatDate(p.date)}</TableCell>
                    <TableCell className="text-right text-xs font-medium">{formatCurrency(p.amount)}</TableCell>
                    <TableCell className="text-xs">{paymentMethodLabel(p.method)}</TableCell>
                    {showPaymentProviderRefs ? (
                      <TableCell className="max-w-[200px] truncate font-mono text-xs text-muted-foreground">
                        {paymentReference(p)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No payment history lines on this invoice.</p>
        )}
      </SectionBlock>
      <SectionBlock title="Reminders sent">
        {invoice.remindersSent && invoice.remindersSent.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {invoice.remindersSent.map((d, i) => (
              <li key={i}>{formatDate(d)}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">None recorded.</p>
        )}
      </SectionBlock>
      {showPlatformAccounting ? (
        <SectionBlock title="Accounting totals">
          {invoice.accountingTotals ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailField label="Lodge transfer (cumulative)">
                {formatCurrency(invoice.accountingTotals.totalLodgeTransfer ?? 0)}
              </DetailField>
              <DetailField label="Platform profit (cumulative)">
                {formatCurrency(invoice.accountingTotals.totalPlatformProfit ?? 0)}
              </DetailField>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None recorded.</p>
          )}
        </SectionBlock>
      ) : null}
      <SectionBlock title="Notes">
        {invoice.note ? (
          <p className="whitespace-pre-wrap text-sm">{invoice.note}</p>
        ) : (
          <p className="text-sm text-muted-foreground">None.</p>
        )}
      </SectionBlock>
      <SectionBlock title="Record timestamps">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Created">{displayText(invoice.createdAt)}</DetailField>
          <DetailField label="Updated">{displayText(invoice.updatedAt)}</DetailField>
          {showAuditFields ? (
            <>
              <DetailField label="Deleted at">{displayText(invoice.deletedAt)}</DetailField>
              <DetailField label="Deleted by uid">{displayMono(invoice.deletedByUid)}</DetailField>
            </>
          ) : null}
        </div>
      </SectionBlock>
    </div>
  );
}

function PaymentDetailsUi({ payment, visibility }: { payment: Payment; visibility: PaymentDetailsVisibility }) {
  const { showPlatformAccounting, showPaymentProviderRefs } = visibility;

  return (
    <div className="space-y-4">
      <SectionBlock title="Payment line">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Amount">{formatCurrency(payment.amount)}</DetailField>
          <DetailField label="Status">
            {payment.voided ? (
              <Badge variant="destructive">Voided</Badge>
            ) : (
              <Badge variant="secondary">Active</Badge>
            )}
          </DetailField>
          <DetailField label="Date">{displayText(payment.date)}</DetailField>
          <DetailField label="Method">{paymentMethodLabel(payment.method)}</DetailField>
          {payment.voided ? (
            <DetailField label="Voided at">{displayText(payment.voidedAt)}</DetailField>
          ) : null}
          <DetailField label="Card processing fee">{formatCurrency(payment.processingFee)}</DetailField>
          {showPaymentProviderRefs ? (
            <DetailField label="Transaction id">{displayMono(payment.transactionId)}</DetailField>
          ) : null}
          <DetailField label="Subtotal (line)">
            {payment.subtotal != null ? formatCurrency(payment.subtotal) : <EmptyValue />}
          </DetailField>
          <DetailField label="VAT (line)">
            {payment.vatAmount != null ? formatCurrency(payment.vatAmount) : <EmptyValue />}
          </DetailField>
          <DetailField label="Transaction fee (line)">
            {payment.transactionFee != null ? formatCurrency(payment.transactionFee) : <EmptyValue />}
          </DetailField>
        </div>
      </SectionBlock>
      {showPaymentProviderRefs ? (
        <SectionBlock title="Provider references">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Stripe payment intent">{displayMono(payment.stripePaymentIntentId)}</DetailField>
            <DetailField label="Stripe charge">{displayMono(payment.stripeChargeId)}</DetailField>
            <DetailField label="SumUp checkout">{displayMono(payment.sumupCheckoutId)}</DetailField>
            <DetailField label="SumUp transaction code">{displayMono(payment.sumupTransactionCode)}</DetailField>
            <DetailField label="SlickPay invoice id">{displayMono(payment.slickpayInvoiceId)}</DetailField>
          </div>
        </SectionBlock>
      ) : null}
      {showPlatformAccounting ? (
        <SectionBlock title="Accounting split (this line)">
          {payment.accountingSplit ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailField label="Lodge transfer">{formatCurrency(payment.accountingSplit.lodgeTransfer)}</DetailField>
              <DetailField label="Platform profit">{formatCurrency(payment.accountingSplit.platformProfit)}</DetailField>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None recorded.</p>
          )}
        </SectionBlock>
      ) : null}
    </div>
  );
}

function SyntheticPaymentUi({
  row,
  note,
  visibility,
}: {
  row: EventPaymentRow;
  note: string | null;
  visibility: PaymentDetailsVisibility;
}) {
  const { showPaymentProviderRefs } = visibility;

  return (
    <div className="space-y-4">
      {note ? <p className="text-sm text-muted-foreground">{note}</p> : null}
      <SectionBlock title="This list row">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Amount">{formatCurrency(row.amount)}</DetailField>
          <DetailField label="Date">{formatDate(row.date)}</DetailField>
          <DetailField label="Method">{row.methodLabel}</DetailField>
          <DetailField label="Reference">
            {showPaymentProviderRefs ? (
              <span className="font-mono text-xs break-all">{row.reference}</span>
            ) : (
              <span className="text-sm text-muted-foreground">Hidden for your role</span>
            )}
          </DetailField>
        </div>
      </SectionBlock>
    </div>
  );
}

export default function PaymentsPage() {
  const { eventId, unitId } = useParams<{ eventId: string; unitId?: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [event, setEvent] = useState<Event | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailRow, setDetailRow] = useState<EventPaymentRow | null>(null);
  const [voidingPayment, setVoidingPayment] = useState(false);

  const normalizeDate = (value: unknown): Date => {
    if (!value) {
      return new Date();
    }
    if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate();
    }
    if (value instanceof Date) {
      return value;
    }
    return new Date(value as string | number | Date);
  };

  useEffect(() => {
    if (!eventId || !unitId) {
      return;
    }

    const eventRef = doc(db, 'units', unitId, 'events', eventId);
    const unsubscribe = onSnapshot(
      eventRef,
      (snap) => {
        if (snap.exists()) {
          setEvent(mapEventData(snap, unitId));
        }
      },
      (error) => {
        console.error('Error listening to event:', error);
        showError('Failed to load event');
      },
    );

    return () => unsubscribe();
  }, [eventId, unitId]);

  useEffect(() => {
    if (!eventId || !unitId) {
      return;
    }

    const unsubscribe = onSnapshot(
      query(collection(db, 'units', unitId, 'events', eventId, 'rsvps'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const rsvpsData = snapshot.docs
          .map((d) => mapRsvpData(d))
          .filter((r): r is RSVP => r !== null);
        setRsvps(rsvpsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching RSVPs:', error);
        showError('Failed to load RSVPs');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [eventId, unitId]);

  useEffect(() => {
    if (!eventId || !unitId) {
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'units', unitId, 'events', eventId, 'invoices'),
      (snapshot) => {
        const invoicesData: Invoice[] = snapshot.docs
          .filter((docSnap) => isActiveInvoiceData(docSnap.data()))
          .map((docSnap) => {
            const data = docSnap.data();
            const paymentHistory: Payment[] = (data.paymentHistory || []).map((payment: Record<string, unknown>) => ({
              amount: (payment.amount as number) || 0,
              date: normalizeDate(payment.date),
              method: (payment.method as Payment['method']) || 'card',
              transactionId: payment.transactionId as string | undefined,
              processingFee: (payment.processingFee as number) || 0,
              subtotal: payment.subtotal as number | undefined,
              vatAmount: payment.vatAmount as number | undefined,
              transactionFee: payment.transactionFee as number | undefined,
              accountingSplit: payment.accountingSplit as Payment['accountingSplit'],
              stripePaymentIntentId: payment.stripePaymentIntentId as string | undefined,
              stripeChargeId: payment.stripeChargeId as string | undefined,
              sumupCheckoutId: payment.sumupCheckoutId as string | undefined,
              sumupTransactionCode: payment.sumupTransactionCode as string | undefined,
              slickpayInvoiceId: payment.slickpayInvoiceId as string | undefined,
              voided: payment.voided === true,
              voidedAt: payment.voidedAt ? normalizeDate(payment.voidedAt) : undefined,
              voidedByUid: payment.voidedByUid as string | undefined,
            }));

            return {
              id: docSnap.id,
              eventId,
              unitId,
              ownerUid: data.ownerUid,
              totalAmount: data.totalAmount || 0,
              amountPaid: data.amountPaid || 0,
              outstandingBalance: data.outstandingBalance || 0,
              status: data.status || 'outstanding',
              rsvps: data.rsvps || [],
              paymentHistory,
              paymentLink: data.paymentLink || '',
              remindersSent: (data.remindersSent || []).map((reminder: unknown) => normalizeDate(reminder)),
              createdAt: normalizeDate(data.createdAt),
              updatedAt: normalizeDate(data.updatedAt),
              invoiceNumber: data.invoiceNumber,
              subtotal: data.subtotal,
              vatAmount: data.vatAmount,
              vatRate: data.vatRate,
              transactionFee: data.transactionFee,
              accountingTotals: data.accountingTotals,
              note: data.note,
              ...(data.pendingVerificationAmount !== undefined
                ? { pendingVerificationAmount: Number(data.pendingVerificationAmount) || 0 }
                : {}),
            };
          });
        invoicesData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setInvoices(invoicesData);
      },
      (error) => {
        console.error('Error fetching invoices:', error);
        showError('Failed to load invoices');
      }
    );

    return () => unsubscribe();
  }, [eventId, unitId]);

  const rsvpMap = useMemo(() => {
    const map = new Map<string, RSVP>();
    rsvps.forEach((rsvp) => map.set(rsvp.id, rsvp));
    return map;
  }, [rsvps]);

  const paymentRows = useMemo((): EventPaymentRow[] => {
    const rows: EventPaymentRow[] = [];
    const invoicesById = new Map(invoices.map((inv) => [inv.id, inv]));
    const rsvpIdsCoveredByInvoiceRows = new Set<string>();

    const markInvoiceRsvpsCovered = (invoice: Invoice) => {
      (invoice.rsvps || []).forEach((id) => rsvpIdsCoveredByInvoiceRows.add(id));
    };

    invoices.forEach((invoice) => {
      const linked = (invoice.rsvps || [])
        .map((id) => rsvpMap.get(id))
        .filter((rsvp): rsvp is RSVP => Boolean(rsvp));
      const payerLabel =
        linked.length > 0
          ? linked.map(formatRsvpDisplayName).join(', ')
          : 'Unknown payer';
      const invoiceLabel = invoice.invoiceNumber || invoice.id;

      (invoice.paymentHistory || []).forEach((payment, index) => {
        rows.push({
          key: `${invoice.id}-history-${index}-${payment.date.getTime()}`,
          invoiceId: invoice.id,
          invoiceLabel,
          payerLabel,
          date: payment.date,
          amount: payment.amount || 0,
          methodLabel: paymentMethodLabel(payment.method),
          reference: paymentReference(payment),
          paymentHistoryIndex: index,
          voided: payment.voided === true,
        });
        if (!payment.voided) {
          markInvoiceRsvpsCovered(invoice);
        }
      });

      const historySum = (invoice.paymentHistory || []).reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
      );
      const effectivePaid = getEffectiveAmountPaid(invoice);
      const remainder = Math.round((effectivePaid - historySum) * 100) / 100;
      if (remainder > AMOUNT_EPS) {
        rows.push({
          key: `${invoice.id}-recorded-${invoice.updatedAt.getTime()}`,
          invoiceId: invoice.id,
          invoiceLabel,
          payerLabel,
          date: invoice.updatedAt,
          amount: remainder,
          methodLabel: 'Recorded manually',
          reference: 'n/a',
          syntheticKind: 'remainder',
        });
        markInvoiceRsvpsCovered(invoice);
      }
    });

    rsvps.forEach((rsvp) => {
      if (!isRsvpMarkedPaid(rsvp)) {
        return;
      }
      if (rsvpIdsCoveredByInvoiceRows.has(rsvp.id)) {
        return;
      }

      const linkedInvoice = rsvp.invoiceId ? invoicesById.get(rsvp.invoiceId) : undefined;
      let amount = getRsvpExpectedAmount(rsvp, event);
      if (amount <= AMOUNT_EPS && linkedInvoice) {
        const n = Math.max(1, (linkedInvoice.rsvps?.length || 1));
        const share = getEffectiveAmountPaid(linkedInvoice) / n;
        const shareFromTotal = (Number(linkedInvoice.totalAmount) || 0) / n;
        amount = Math.max(share, shareFromTotal);
      }

      rows.push({
        key: `rsvp-fallback-${rsvp.id}`,
        invoiceId: linkedInvoice?.id ?? '',
        invoiceLabel: linkedInvoice ? linkedInvoice.invoiceNumber || linkedInvoice.id : 'n/a',
        payerLabel: formatRsvpDisplayName(rsvp),
        date: rsvp.updatedAt || new Date(),
        amount: Math.round(amount * 100) / 100,
        methodLabel: amount > AMOUNT_EPS ? 'Recorded manually' : 'Paid (amount not available)',
        reference: 'n/a',
        syntheticKind: 'fallback',
        fallbackRsvpId: rsvp.id,
      });
    });

    rows.sort((a, b) => b.date.getTime() - a.date.getTime());
    return rows;
  }, [invoices, rsvpMap, rsvps, event]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) {
      return paymentRows;
    }
    const q = searchTerm.toLowerCase().trim();
    return paymentRows.filter((row) => {
      const method = row.methodLabel.toLowerCase();
      const amountStr = row.amount.toFixed(2);
      const tx = row.reference.toLowerCase() === 'n/a' ? '' : row.reference;
      return (
        row.payerLabel.toLowerCase().includes(q) ||
        row.invoiceLabel.toLowerCase().includes(q) ||
        method.includes(q) ||
        amountStr.includes(q) ||
        tx.toLowerCase().includes(q)
      );
    });
  }, [paymentRows, searchTerm]);

  const totalReceived = useMemo(
    () =>
      paymentRows.reduce(
        (sum, row) => (row.voided ? sum : sum + (row.amount || 0)),
        0,
      ),
    [paymentRows],
  );

  const handleVoidPayment = useCallback(async () => {
    if (!detailRow || !unitId || !eventId || detailRow.paymentHistoryIndex === undefined) {
      return;
    }
    if (detailRow.voided) {
      return;
    }

    const result = await confirmAction({
      title: 'Void payment?',
      text: `Remove ${formatCurrency(detailRow.amount)} from recorded takings on invoice ${detailRow.invoiceLabel}? The invoice balance will be updated.`,
      confirmButtonText: 'Void payment',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) {
      return;
    }

    setVoidingPayment(true);
    try {
      const fn = httpsCallable(functions, 'voidInvoicePayment');
      await fn({
        unitId,
        eventId,
        invoiceId: detailRow.invoiceId,
        paymentHistoryIndex: detailRow.paymentHistoryIndex,
      });
      showSuccess('Payment voided');
      setDetailRow(null);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Failed to void payment';
      console.error(err);
      showError(message);
    } finally {
      setVoidingPayment(false);
    }
  }, [detailRow, unitId, eventId]);

  const detailResolved = useMemo(() => {
    if (!detailRow) {
      return null;
    }
    return resolvePaymentRowDetails(detailRow, invoices, rsvpMap);
  }, [detailRow, invoices, rsvpMap]);

  const detailVisibility = useMemo(
    () => getPaymentDetailsVisibility(unitId, userProfile ?? null),
    [unitId, userProfile]
  );

  const detailModalShowsReducedFields = useMemo(() => {
    const v = detailVisibility;
    return !v.showInternalIdentifiers || !v.showPlatformAccounting || !v.showPaymentProviderRefs;
  }, [detailVisibility]);

  const handleExportCsv = useCallback(async () => {
    if (filteredRows.length === 0) {
      showError('Nothing to export. Try clearing the search or wait for payments to load.');
      return;
    }
    if (unitId) {
      try {
        const gate = httpsCallable(functions, 'assertUnitExportAllowed');
        await gate({ unitId });
      } catch (err: unknown) {
        const details =
          err && typeof err === 'object' && 'details' in err
            ? (err as { details?: { code?: string } }).details
            : undefined;
        if (details?.code === 'CREDIT_DEBT') {
          showError(
            'Exports are paused until the unit credit balance is brought back to zero or above. Please ask your secretary or treasurer to top up.',
          );
          return;
        }
        console.error(err);
        showError('Could not verify export eligibility.');
        return;
      }
    }
    const csv = buildPaymentsCsv(filteredRows.filter((row) => !row.voided));
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    const base = slugifyFilenameSegment(event?.name ?? 'event');
    anchor.download = `${base}-payments-${eventId}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    const filterNote = searchTerm.trim() ? ' (current search filter)' : '';
    showSuccess(`Exported ${filteredRows.length} row${filteredRows.length === 1 ? '' : 's'} to CSV${filterNote}.`);
  }, [filteredRows, event?.name, eventId, searchTerm, unitId]);

  if (!eventId || !unitId) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main className="flex-1 w-full min-w-0 py-6 sm:py-8">
          <DashboardContentContainer>
            <p className="text-muted-foreground">Invalid event.</p>
          </DashboardContentContainer>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 w-full min-w-0 py-6 sm:py-8">
        <DashboardContentContainer>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Payments</h1>
              <p className="text-muted-foreground">
                {event?.name ? (
                  <>
                    Recorded payments for <span className="text-foreground">{event.name}</span>
                  </>
                ) : (
                  'Recorded payments for this event'
                )}
              </p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Payment history</CardTitle>
            <CardDescription>
              All payments recorded against invoices for this event ({paymentRows.length}{' '}
              {paymentRows.length === 1 ? 'payment' : 'payments'}
              {paymentRows.length > 0 ? `, ${formatCurrency(totalReceived)} total` : ''}).
            </CardDescription>
            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:gap-4">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by payer, invoice, amount, or method…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Filter payments"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="shrink-0 sm:self-auto"
                onClick={handleExportCsv}
                disabled={loading || filteredRows.length === 0}
                aria-label="Export payments to CSV"
              >
                <Download className="mr-2 h-4 w-4" aria-hidden />
                Export to CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : filteredRows.length === 0 ? (
              <p className="text-muted-foreground">
                {paymentRows.length === 0
                  ? 'No payments have been recorded for this event yet.'
                  : 'No payments match your search.'}
              </p>
            ) : (
              <>
                <div className="space-y-3 md:hidden">
                  {filteredRows.map((row) => (
                    <Card key={row.key} className={row.voided ? 'opacity-60' : undefined}>
                      <CardContent className="space-y-2 p-4 text-sm">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-muted-foreground">
                            {formatDate(row.date)}
                            {row.voided ? (
                              <Badge variant="destructive" className="ml-2">
                                Voided
                              </Badge>
                            ) : null}
                          </span>
                          <span className={`font-semibold ${row.voided ? 'line-through' : ''}`}>
                            {formatCurrency(row.amount)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <span className="text-muted-foreground">Payer: </span>
                          <span className="break-words">{row.payerLabel}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Invoice: </span>
                          <span className="font-mono text-xs break-all">{row.invoiceLabel}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Method: </span>
                          {row.methodLabel}
                        </div>
                        <div className="break-all font-mono text-xs text-muted-foreground">
                          <span className="font-sans text-foreground">Reference: </span>
                          {row.reference}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => setDetailRow(row)}
                        >
                          <Eye className="mr-2 h-4 w-4" aria-hidden />
                          View details
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="hidden overflow-x-auto md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Payer</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="hidden lg:table-cell">Reference</TableHead>
                        <TableHead className="w-[120px] text-right">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRows.map((row) => (
                        <TableRow key={row.key} className={row.voided ? 'opacity-60' : undefined}>
                          <TableCell className="whitespace-nowrap">
                            {formatDate(row.date)}
                            {row.voided ? (
                              <Badge variant="destructive" className="ml-2">
                                Voided
                              </Badge>
                            ) : null}
                          </TableCell>
                          <TableCell>{row.payerLabel}</TableCell>
                          <TableCell className="font-mono text-sm">{row.invoiceLabel}</TableCell>
                          <TableCell>{row.methodLabel}</TableCell>
                          <TableCell className="text-right font-medium">
                            <span className={row.voided ? 'line-through' : undefined}>
                              {formatCurrency(row.amount)}
                            </span>
                          </TableCell>
                          <TableCell className="hidden max-w-[220px] truncate font-mono text-xs text-muted-foreground lg:table-cell">
                            {row.reference}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setDetailRow(row)}
                            >
                              <Eye className="mr-1.5 h-4 w-4" aria-hidden />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={detailRow !== null} onOpenChange={(open) => !open && setDetailRow(null)}>
          <DialogContent className="flex max-h-[90vh] w-[calc(100vw-1.5rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
            <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12 text-left">
              <DialogTitle>Payment details</DialogTitle>
              <DialogDescription>
                Guest RSVP, invoice, and payment information for this list entry.
              </DialogDescription>
              {detailModalShowsReducedFields ? (
                <p className="pt-2 text-xs text-muted-foreground">
                  Technical identifiers, payment gateway references, audit fields, and platform accounting are only
                  shown to the unit secretary, treasurer, or platform administrator.
                </p>
              ) : null}
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              {detailRow && detailResolved && (
                <div className="space-y-8">
                  <div>
                    <h3 className="mb-4 text-base font-semibold text-foreground">RSVP</h3>
                    {detailResolved.rsvps.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No RSVP documents are linked to this entry in the loaded data.
                      </p>
                    ) : (
                      detailResolved.rsvps.map((r) => (
                        <div key={r.id} className="mb-8 space-y-4 last:mb-0">
                          <p className="text-sm font-medium text-foreground">
                            {formatRsvpDisplayName(r)}
                            {detailVisibility.showInternalIdentifiers ? (
                              <span className="font-mono text-xs font-normal text-muted-foreground"> ({r.id})</span>
                            ) : null}
                          </p>
                          <RsvpDetailsUi rsvp={r} visibility={detailVisibility} />
                        </div>
                      ))
                    )}
                  </div>

                  <div className="border-t border-border pt-8">
                    <h3 className="mb-4 text-base font-semibold text-foreground">Invoice</h3>
                    {!detailResolved.invoice ? (
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p>No invoice document found for this entry.</p>
                        {detailRow.invoiceId && detailVisibility.showInternalIdentifiers ? (
                          <p className="font-mono text-xs">Invoice id from row: {detailRow.invoiceId}</p>
                        ) : null}
                      </div>
                    ) : (
                      <InvoiceDetailsUi
                        invoice={detailResolved.invoice}
                        visibility={detailVisibility}
                        rsvpById={rsvpMap}
                        event={event}
                        allRsvps={rsvps}
                      />
                    )}
                  </div>

                  <div className="border-t border-border pt-8">
                    <h3 className="mb-4 text-base font-semibold text-foreground">Payment</h3>
                    {detailResolved.payment ? (
                      <PaymentDetailsUi payment={detailResolved.payment} visibility={detailVisibility} />
                    ) : (
                      <SyntheticPaymentUi
                        row={detailRow}
                        note={detailResolved.syntheticNote}
                        visibility={detailVisibility}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="shrink-0 border-t px-6 py-4 sm:justify-between">
              {detailRow &&
              detailRow.paymentHistoryIndex !== undefined &&
              !detailRow.voided &&
              detailVisibility.showInternalIdentifiers ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={voidingPayment}
                  onClick={() => void handleVoidPayment()}
                >
                  <Ban className="mr-2 h-4 w-4" aria-hidden />
                  {voidingPayment ? 'Voiding…' : 'Void payment'}
                </Button>
              ) : (
                <span />
              )}
              <Button type="button" variant="secondary" onClick={() => setDetailRow(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </DashboardContentContainer>
      </main>
    </div>
  );
}
